import { Button } from 'flowbite-react';
import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  file: Blob | string;
  className?: string;
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  file,
  className = '',
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();

    const resizeObserver = new window.ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const changePage = (offset: number) => {
    setPageNumber((prevPageNumber) => prevPageNumber + offset);
  };

  const previousPage = () => {
    changePage(-1);
  };

  const nextPage = () => {
    changePage(1);
  };

  const zoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.5));
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Панель управления */}
      <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-4">
          <Button
            color="secondary"
            onClick={previousPage}
            disabled={pageNumber <= 1}
          >
            Предыдущая
          </Button>
          <span className="text-sm">
            Страница {pageNumber} из {numPages}
          </span>
          <Button
            color="secondary"
            onClick={nextPage}
            disabled={pageNumber >= numPages}
          >
            Следующая
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            -
          </button>
          <span className="text-sm min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            +
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex w-full justify-center border border-gray-200 rounded-lg overflow-auto"
      >
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={(error) => {
            console.error('Error loading PDF:', error);
          }}
          loading={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2">Загрузка PDF...</span>
            </div>
          }
          error={
            <div className="flex items-center justify-center p-8 text-red-500">
              Ошибка загрузки PDF файла
            </div>
          }
        >
          <Page
            pageNumber={pageNumber}
            width={containerWidth ? containerWidth * scale : undefined}
            loading={
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2">Загрузка страницы...</span>
              </div>
            }
          />
        </Document>
      </div>
    </div>
  );
};
